import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Spacer
} from "../../../../components";
import { FC } from "react";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { ProjectLaborProductMeasurement, SelectContractorInput, AssociatedContractor, SelectLaborVariantInput } from "../../../../redux/types/Material";
import { ArrowBack, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { SELECT_CONTRACTOR, SELECT_LABOR_VARIANT } from "../../../../apolloClient/operation/mutations/mutation";
import { useParams } from "react-router-dom";
import { LaborVariantModal } from "./LaborVariantModal";

export function isContractorSelected(contractor: AssociatedContractor): boolean {
  if (contractor.isSelected) return true;
  const labor = contractor.associatedLabor?.[0];
  if (!labor) return false;
  if (labor.isSelected) return true;
  return labor.variants?.some((v) => v.isSelected) ?? false;
}

export function getSelectedLabel(contractor: AssociatedContractor): string | null {
  const labor = contractor.associatedLabor?.[0];
  if (!labor) return null;

  if (labor.isSelected) return "Base labor";
  const selectedVar = labor.variants?.find((v) => v.isSelected);
  if (selectedVar) return selectedVar.description;

  return null;
}

interface ContractorShopProps {
  handleRefetchProjectDetails: Function;
  projectData?: CustomerProjectDetails;
  selectedProduct: ProjectLaborProductMeasurement;
  onBack: () => void;
}

const ContractorShop: FC<ContractorShopProps> = ({
  handleRefetchProjectDetails,
  projectData,
  selectedProduct,
  onBack,
}) => {
  const { id: projectId } = useParams<{ id: string }>();
  const [selectingContractorId, setSelectingContractorId] = useState<number | null>(null);
  const [selectedContractorForModal, setSelectedContractorForModal] = useState<AssociatedContractor | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // Apollo mutations
  const [selectContractor] = useMutation<
    { selectContractor: boolean },
    { input: SelectContractorInput }
  >(SELECT_CONTRACTOR);

  const [selectLaborVariant] = useMutation<
    { selectLaborVariant: boolean },
    { input: SelectLaborVariantInput }
  >(SELECT_LABOR_VARIANT);

  // Scroll to top when component mounts
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, []);

  const { allContractors, measurementContractorMap } = React.useMemo(() => {
    const consts: AssociatedContractor[] = [];
    const map: { [contractorId: number]: number } = {};
    selectedProduct.measurements?.forEach((measurement) => {
      measurement.associatedContractor?.forEach((contractor) => {
        if (!consts.find(c => c.id === contractor.id)) {
          consts.push(contractor);
          map[contractor.id] = measurement.id;
        }
      });
    });
    return { allContractors: consts, measurementContractorMap: map };
  }, [selectedProduct.measurements]);

  const chooseContractor = useCallback(async (contractorId: number) => {
    if (contractorId <= 0) return;

    if (!projectId) {
      toast("Project ID not found", { type: "error", position: "top-right" });
      return;
    }

    const contractor = allContractors.find(c => c.id === contractorId);
    if (!contractor) return;

    const labor = contractor.associatedLabor?.[0];

    if (labor) {
      setSelectedContractorForModal(contractor);
      return;
    }

    const measurementId = measurementContractorMap[contractorId];
    if (!measurementId) {
      toast("Measurement not found for this contractor", { type: "error", position: "top-right" });
      return;
    }

    setSelectingContractorId(contractorId);

    try {
      await selectContractor({
        variables: {
          input: {
            projectId: parseInt(projectId, 10),
            productId: selectedProduct.productId,
            contractorId: contractorId,
          },
        },
      });

      toast("Contractor selected successfully!", { type: "success", position: "top-right" });
      await handleRefetchProjectDetails();
      onBack();

    } catch (error) {
      console.error("Error selecting contractor:", error);
      toast("Failed to select contractor", { type: "error", position: "top-right" });
    } finally {
      setSelectingContractorId(null);
    }
  }, [allContractors, measurementContractorMap, handleRefetchProjectDetails, projectId, selectedProduct.productId, selectContractor, onBack]);

  const confirmVariantSelection = async (variantId: number) => {
    if (!selectedContractorForModal || !projectId) return;
    const contractorId = selectedContractorForModal.id;
    const measurementId = measurementContractorMap[contractorId];
    const labor = selectedContractorForModal.associatedLabor?.[0];

    if (!measurementId || !labor) {
      toast("Required data missing for this contractor", { type: "error", position: "top-right" });
      return;
    }

    setSelectingContractorId(contractorId);

    try {
      const hasVariants = (labor.variants?.length ?? 0) > 0;

      if (!hasVariants) {
        await selectContractor({
          variables: {
            input: {
              projectId: parseInt(projectId, 10),
              productId: selectedProduct.productId,
              contractorId: contractorId,
            },
          },
        });
      } else {
        await selectLaborVariant({
          variables: {
            input: {
              projectId: parseInt(projectId, 10),
              productId: selectedProduct.productId,
              laborId: labor.id,
              laborVariantId: variantId,
              measurementId: measurementId,
            },
          },
        });
      }

      toast("Contractor selected successfully!", { type: "success", position: "top-right" });
      await handleRefetchProjectDetails();
      setSelectedContractorForModal(null);
      onBack();
    } catch (error) {
      console.error("Error selecting contractor variant:", error);
      toast("Failed to select contractor variant", { type: "error", position: "top-right" });
    } finally {
      setSelectingContractorId(null);
    }
  };

  // Main contractor shop view - show contractors directly
  return (
    <div
      ref={topRef}
      style={{
        padding: window.innerWidth < 768 ? "16px" : "24px",
        backgroundColor: "#f8fafc",
        minHeight: "100%",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Back Button and Breadcrumb */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            borderRadius: "6px",
            backgroundColor: "#3b82f6",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <ArrowBack style={{ width: "20px", height: "20px" }} />
          Back
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "14px", color: "#1f2937" }}>Contractor selection /</span>
          <span style={{ fontSize: "14px", color: "#6b7280" }}>
            {selectedProduct.name?.charAt(0).toUpperCase() + selectedProduct.name?.slice(1).toLowerCase()}
          </span>
        </div>
      </div>

      {/* Contractors List */}
      <div style={{ marginBottom: "32px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "500", color: "#1f2937", marginBottom: "8px" }}>
          Available Contractors
        </h3>
        <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "16px", width: "100%" }}>
          Select a contractor for {selectedProduct.name}.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
          {allContractors.map((contractor) => (
            <div
              key={contractor.id}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                padding: window.innerWidth < 768 ? "12px" : "16px",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: window.innerWidth < 768 ? "column" : "row",
                alignItems: window.innerWidth < 768 ? "stretch" : "center",
                gap: window.innerWidth < 768 ? "12px" : "0",
              }}
              className="contractor-card"
            >
              {contractor.files && contractor.files.length > 0 ? (
                <div
                  style={{
                    flexShrink: 0,
                    width: window.innerWidth < 768 ? "100%" : "192px",
                    height: window.innerWidth < 768 ? "160px" : "192px",
                    marginRight: window.innerWidth < 768 ? "0" : "16px",
                  }}
                >
                  <img
                    src={contractor.files[0].url}
                    alt={contractor.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "6px",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    flexShrink: 0,
                    width: window.innerWidth < 768 ? "100%" : "192px",
                    height: window.innerWidth < 768 ? "120px" : "192px",
                    marginRight: window.innerWidth < 768 ? "0" : "16px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #d1d5db",
                  }}
                >
                  <div style={{ textAlign: "center", color: "#9ca3af" }}>
                    <div style={{ fontSize: window.innerWidth < 768 ? "36px" : "48px", marginBottom: "8px" }}>🏢</div>
                    <div style={{ fontSize: "12px" }}>No Image</div>
                  </div>
                </div>
              )}

              {/* Contractor Details */}
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    fontSize: window.innerWidth < 768 ? "16px" : "18px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "8px",
                    textTransform: "capitalize",
                    wordBreak: "break-word",
                  }}
                >
                  {contractor.name?.charAt(0).toUpperCase() + contractor.name?.slice(1).toLowerCase()}
                </h3>

                {contractor.total ? (
                  <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>
                    <strong>Estimated Cost:</strong> ${contractor.total.toLocaleString()}
                  </p>
                ) : (
                  <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>
                    <strong>Estimated Cost:</strong> Contact for quote
                  </p>
                )}

                {/* Description */}
                <p className="text-wrap" style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>
                  <strong>Description:</strong> {contractor.description || "No description provided"}
                </p>

                {/* Website */}
                <div style={{ fontSize: window.innerWidth < 768 ? "13px" : "14px", color: "#6b7280", marginBottom: "8px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                  <strong>Website:</strong>{" "}
                  {contractor.website ? (
                    <>
                      <a
                        href={contractor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#3b82f6", textDecoration: "underline", marginLeft: "4px" }}
                      >
                        {contractor.website}
                      </a>
                      <span style={{ marginLeft: "8px", height: "15px", width: "15px" }}>
                        <img
                          src="https://imagedelivery.net/ljlAKBP_91m1yO7rrhjKgQ/11b4d426-a40e-434b-de2b-7c540e78b300/chatImage"
                          alt="External link"
                          style={{ height: "15px", width: "15px" }}
                        />
                      </span>
                    </>
                  ) : (
                    <span style={{ marginLeft: "4px" }}>Not provided</span>
                  )}
                </div>

                {contractor.trades && contractor.trades.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", marginTop: "8px", marginBottom: "16px" }}>
                    {contractor.trades.map((trade, tradeIndex) => (
                      <span
                        key={tradeIndex}
                        style={{
                          fontSize: "12px",
                          color: "#fff",
                          backgroundColor: "#3b82f6",
                          padding: "4px 12px",
                          borderRadius: "9999px",
                          marginRight: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        {trade.name}
                      </span>
                    ))}
                  </div>
                ) : ('')}

                <button
                  onClick={() => chooseContractor(contractor.id)}
                  disabled={selectingContractorId === contractor.id}
                  style={{
                    marginTop: "16px",
                    padding: window.innerWidth < 768 ? "12px 16px" : "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    width: window.innerWidth < 768 ? "100%" : "auto",
                    fontWeight: "500",
                    border: "none",
                    cursor: selectingContractorId === contractor.id ? "not-allowed" : "pointer",
                    backgroundColor: isContractorSelected(contractor) ? "#1f2937" : selectingContractorId === contractor.id ? "#9ca3af" : "#e5e7eb",
                    color: isContractorSelected(contractor) || selectingContractorId === contractor.id ? "#fff" : "#1f2937",
                    transition: "all 0.2s ease",
                    opacity: selectingContractorId === contractor.id ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isContractorSelected(contractor) && selectingContractorId !== contractor.id) {
                      e.currentTarget.style.backgroundColor = "#1f2937";
                      e.currentTarget.style.color = "#fff";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isContractorSelected(contractor) && selectingContractorId !== contractor.id) {
                      e.currentTarget.style.backgroundColor = "#e5e7eb";
                      e.currentTarget.style.color = "#1f2937";
                    }
                  }}
                >
                  {isContractorSelected(contractor) ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <CheckCircle style={{ width: 18, height: 18 }} />
                      Selected: {getSelectedLabel(contractor) || "Contractor"}
                    </span>
                  ) : selectingContractorId === contractor.id ? "Selecting..." : "Select Contractor"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* No Contractors Message */}
      {allContractors.length === 0 && (
        <div style={{ marginBottom: "32px" }}>
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "2px dashed #d1d5db",
              borderRadius: "12px",
              padding: "48px 24px",
              textAlign: "center",
              color: "#6b7280",
              maxWidth: "600px",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              No Contractors Available
            </h3>
            <p
              className="text-wrap"
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              No contractors have been configured for this product yet.
              Please contact your project manager for more information.
            </p>
          </div>
        </div>
      )}

      {selectedContractorForModal && selectedContractorForModal.associatedLabor?.[0] && (
        <LaborVariantModal
          open={!!selectedContractorForModal}
          onClose={() => setSelectedContractorForModal(null)}
          onSelect={confirmVariantSelection}
          labor={selectedContractorForModal.associatedLabor[0]}
          isSelecting={selectingContractorId === selectedContractorForModal.id}
        />
      )}

      <Spacer size={3} direction="col" />
    </div>
  );
};

export { ContractorShop };
